import React, { useEffect, useState } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';

import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { CadernoLPMAAssinatura, CadernoLPMAAvaliacaoAgronomica } from '../../models/modelsCadernoLPMA';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';
import api from '../../services/api';
import AssinaturaComNomesAdicionais, {
	parseNomesEnvolvidosJson,
	stringifyNomesEnvolvidos,
} from './components/AssinaturaComNomesAdicionais';
import MetodoLimpezaSelect from './components/MetodoLimpezaSelect';
import {
	formatarMetodoLimpezaParaJson,
	normalizarMetodoLimpezaParaFormulario,
} from '../../util/cadernoLPMA/metodoLimpeza';

interface AvaliacaoAgronomicaModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMAAvaliacaoAgronomica) => void;
	dadosAvaliacaoAgronomica?: CadernoLPMAAvaliacaoAgronomica | null;
	assinaturasDisponiveis?: CadernoLPMAAssinatura[];
}

const defaultValues: CadernoLPMAAvaliacaoAgronomica = {
	dataInicioAvaliacaoAgronomica: null,
	dataTerminoAvaliacaoAgronomica: null,
	localidadeAvaliacaoAgronomica: '',
	responsavelAvaliacaoAgronomica: '',
	tipoMaterialAvaliadoGraos: false,
	tipoMaterialAvaliadoRaizes: false,
	tipoMaterialAvaliadoFolhas: false,
	tipoMaterialAvaliadoVagem: false,
	tipoMaterialAvaliadoCapulho: false,
	tipoMaterialAvaliadoOutros: false,
	outrosTipoMaterialAvaliado: '',
	tipoAvaliacaoPesagem: false,
	tipoAvaliacaoDeterminacaoUmidade: false,
	tipoAvaliacaoContagem: false,
	tipoAvaliacaoDescarocamento: false,
	tipoAvaliacaoDeterminacaoPluma: false,
	tipoAvaliacaoPreparoAmostras: false,
	tipoAvaliacaoTratamento: false,
	tipoAvaliacaoMoagem: false,
	tipoAvaliacaoOutros: false,
	outrosTipoAvaliacao: '',
	observacoesAvaliacaoAgronomica: '',
};

const formatarDataParaJson = (valor: any) => {
	if (!valor) return null;
	const data = valor instanceof Date ? valor : new Date(valor);
	return Number.isNaN(data.getTime()) ? null : data.toISOString();
};

export default function AvaliacaoAgronomicaModal({
	show,
	onHide,
	onSubmit,
	dadosAvaliacaoAgronomica,
	assinaturasDisponiveis = [],
}: AvaliacaoAgronomicaModalProps) {
	const {
		register,
		handleSubmit,
		control,
		reset,
		getValues,
		setValue,
		watch,
		formState: { errors },
	} = useForm<CadernoLPMAAvaliacaoAgronomica>({
		mode: 'all',
		defaultValues,
	});

	const [quantidadeLinhasEquipamentos, setQuantidadeLinhasEquipamentos] = useState<number>(1);
	const [quantidadeLinhasInstalacoes, setQuantidadeLinhasInstalacoes] = useState<number>(1);

	// Estados para assinatura
	const [assinaturaEnvolvidos, setAssinaturaEnvolvidos] = useState<string | null>(null);
	const [assinaturaUsuario, setAssinaturaUsuario] = useState<string | null>(null);
	const [mostrarAssinatura, setMostrarAssinatura] = useState(false);
	const [nomesAdicionais, setNomesAdicionais] = useState<string[]>([]);

	// Carregar assinatura do usuário
	useEffect(() => {
		if (show) {
			const carregarAssinatura = async () => {
				try {
					const token = localStorage.getItem('token');
					if (token) {
						const decoded: any = JSON.parse(atob(token.split('.')[1]));
						const username = decoded.unique_name || '';
						
						const { data } = await api.get(`/api/v1/admin/usuarios/assinatura/${username}`);
						if (data && data.assinaturaBase64) {
							setAssinaturaUsuario(data.assinaturaBase64);
						}
					}
				} catch (error) {
					// Usuário não tem assinatura cadastrada
				}
			};
			
			carregarAssinatura();
			
			// Carregar assinatura salva se existir
			if (dadosAvaliacaoAgronomica?.assinaturaEnvolvidosBase64) {
				setAssinaturaEnvolvidos(dadosAvaliacaoAgronomica.assinaturaEnvolvidosBase64);
			} else {
				// Limpar se não houver assinatura salva
				setAssinaturaEnvolvidos(null);
			}
			setNomesAdicionais(parseNomesEnvolvidosJson(dadosAvaliacaoAgronomica?.nomesEnvolvidosAdicionaisJson));
		} else {
			setMostrarAssinatura(false);
			setNomesAdicionais([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [show, dadosAvaliacaoAgronomica?.assinaturaEnvolvidosBase64, dadosAvaliacaoAgronomica?.nomesEnvolvidosAdicionaisJson]);

	useEffect(() => {
		if (!show) {
			reset(defaultValues);
			setQuantidadeLinhasEquipamentos(1);
			setQuantidadeLinhasInstalacoes(1);
			setAssinaturaEnvolvidos(null);
			setMostrarAssinatura(false);
			setNomesAdicionais([]);
		} else if (dadosAvaliacaoAgronomica) {
			const parseDate = (valor: any) => {
				if (!valor) return null;
				const data = new Date(valor);
				return Number.isNaN(data.getTime()) ? null : data;
			};

			// Parse dos JSONs
			let equipamentos: any[] = [];
			let instalacoes: any[] = [];
			try {
				if (dadosAvaliacaoAgronomica.limpezaEquipamentosAgronomicaJson) {
					equipamentos = JSON.parse(dadosAvaliacaoAgronomica.limpezaEquipamentosAgronomicaJson);
				}
			} catch (e) {}
			try {
				if (dadosAvaliacaoAgronomica.limpezaInstalacoesAgronomicaJson) {
					instalacoes = JSON.parse(dadosAvaliacaoAgronomica.limpezaInstalacoesAgronomicaJson);
				}
			} catch (e) {}

			// Atualiza quantidade de linhas
			setQuantidadeLinhasEquipamentos(Math.max(1, equipamentos.length));
			setQuantidadeLinhasInstalacoes(Math.max(1, instalacoes.length));

			// Prepara dados para resetar o formulário
			const dadosParaReset: any = {
				...dadosAvaliacaoAgronomica,
				dataInicioAvaliacaoAgronomica: parseDate(dadosAvaliacaoAgronomica.dataInicioAvaliacaoAgronomica),
				dataTerminoAvaliacaoAgronomica: parseDate(dadosAvaliacaoAgronomica.dataTerminoAvaliacaoAgronomica),
			};

			// Popular campos de equipamentos
			equipamentos.forEach((item: any, index: number) => {
				const metodoLimpeza = normalizarMetodoLimpezaParaFormulario(item.metodo);
				dadosParaReset[`equipamentoNome${index}`] = item.nome || '';
				dadosParaReset[`equipamentoDataAntes${index}`] = parseDate(item.dataAntes);
				dadosParaReset[`equipamentoDataDepois${index}`] = parseDate(item.dataDepois);
				dadosParaReset[`equipamentoMetodo${index}`] = metodoLimpeza.metodo;
				dadosParaReset[`equipamentoMetodoOutros${index}`] = metodoLimpeza.outros;
				dadosParaReset[`equipamentoResponsavel${index}`] = item.responsavel || '';
			});

			// Popular campos de instalações
			instalacoes.forEach((item: any, index: number) => {
				dadosParaReset[`instalacaoNome${index}`] = item.nome || '';
				dadosParaReset[`instalacaoData${index}`] = parseDate(item.data);
				dadosParaReset[`instalacaoResponsavel${index}`] = item.responsavel || '';
			});

			reset(dadosParaReset);
			setNomesAdicionais(parseNomesEnvolvidosJson(dadosAvaliacaoAgronomica.nomesEnvolvidosAdicionaisJson));
		}
	}, [show, dadosAvaliacaoAgronomica, reset]);

	const adicionarLinhaEquipamento = () => setQuantidadeLinhasEquipamentos((prev) => prev + 1);
	const adicionarLinhaInstalacao = () => setQuantidadeLinhasInstalacoes((prev) => prev + 1);

	const removerLinhaEquipamento = (index: number) => {
		if (quantidadeLinhasEquipamentos === 1) return;
		setQuantidadeLinhasEquipamentos((prev) => prev - 1);
		limparCamposEquipamento(index);
	};

	const removerLinhaInstalacao = (index: number) => {
		if (quantidadeLinhasInstalacoes === 1) return;
		setQuantidadeLinhasInstalacoes((prev) => prev - 1);
		limparCamposInstalacao(index);
	};

	const limparCamposEquipamento = (index: number) => {
		setValue(`equipamentoNome${index}` as any, '');
		setValue(`equipamentoDataAntes${index}` as any, null);
		setValue(`equipamentoDataDepois${index}` as any, null);
		setValue(`equipamentoMetodo${index}` as any, '');
		setValue(`equipamentoMetodoOutros${index}` as any, '');
		setValue(`equipamentoResponsavel${index}` as any, '');
	};

	const limparCamposInstalacao = (index: number) => {
		setValue(`instalacaoNome${index}` as any, '');
		setValue(`instalacaoData${index}` as any, null);
		setValue(`instalacaoResponsavel${index}` as any, '');
	};

	const handleSubmitInterno = (dados: CadernoLPMAAvaliacaoAgronomica) => {
		const valores = getValues() as Record<string, any>;
		const dadosNormalizados: Record<string, any> = { ...dados };

		const equipamentos: any[] = [];
		for (let i = 0; i < quantidadeLinhasEquipamentos; i++) {
			const nome = valores[`equipamentoNome${i}`];
			const dataAntes = valores[`equipamentoDataAntes${i}`];
			const dataDepois = valores[`equipamentoDataDepois${i}`];
			const metodo = formatarMetodoLimpezaParaJson(
				valores[`equipamentoMetodo${i}`],
				valores[`equipamentoMetodoOutros${i}`],
			);
			const responsavel = valores[`equipamentoResponsavel${i}`];

			if (nome || dataAntes || dataDepois || metodo || responsavel) {
				equipamentos.push({
					nome,
					dataAntes: formatarDataParaJson(dataAntes),
					dataDepois: formatarDataParaJson(dataDepois),
					metodo,
					responsavel,
				});
			}

			delete dadosNormalizados[`equipamentoNome${i}`];
			delete dadosNormalizados[`equipamentoDataAntes${i}`];
			delete dadosNormalizados[`equipamentoDataDepois${i}`];
			delete dadosNormalizados[`equipamentoMetodo${i}`];
			delete dadosNormalizados[`equipamentoMetodoOutros${i}`];
			delete dadosNormalizados[`equipamentoResponsavel${i}`];
		}

		const instalacoes: any[] = [];
		for (let i = 0; i < quantidadeLinhasInstalacoes; i++) {
			const nome = valores[`instalacaoNome${i}`];
			const data = valores[`instalacaoData${i}`];
			const responsavel = valores[`instalacaoResponsavel${i}`];

			if (nome || data || responsavel) {
				instalacoes.push({
					nome,
					data: formatarDataParaJson(data),
					responsavel,
				});
			}

			delete dadosNormalizados[`instalacaoNome${i}`];
			delete dadosNormalizados[`instalacaoData${i}`];
			delete dadosNormalizados[`instalacaoResponsavel${i}`];
		}

		dadosNormalizados.limpezaEquipamentosAgronomicaJson = equipamentos.length ? JSON.stringify(equipamentos) : null;
		dadosNormalizados.limpezaInstalacoesAgronomicaJson = instalacoes.length ? JSON.stringify(instalacoes) : null;
		dadosNormalizados.assinaturaEnvolvidosBase64 = assinaturaEnvolvidos || null;
		dadosNormalizados.nomesEnvolvidosAdicionaisJson = stringifyNomesEnvolvidos(nomesAdicionais);

		onSubmit(dadosNormalizados as CadernoLPMAAvaliacaoAgronomica);
		onHide();
	};

	return (
		<Modal show={show} onHide={onHide} title={STRINGS_CADERNOLPMA.AVALIACAOAGRONOMICA_MODAL_TITLE} bodyClassName="caderno-lpma-modal-body">
			<Form onSubmit={handleSubmit(handleSubmitInterno)} autoComplete="off">

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Registro de acesso dos envolvidos e datas inicial/final de realização das avaliações</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom">
					<Col md={6}>
						<Form.Group>
							<Form.Label>Localidade / Instalação</Form.Label>
							<Form.Control
								ref={register}
								name="localidadeAvaliacaoAgronomica"
								placeholder="Informe a localidade ou instalação utilizada"
								maxLength={200}
							/>
							{errors.localidadeAvaliacaoAgronomica && <span className="error-field">{errors.localidadeAvaliacaoAgronomica.message}</span>}
						</Form.Group>
					</Col>
					<Col md={3}>
						<Form.Group>
							<Form.Label>Data inicial</Form.Label>
							<Controller
								control={control}
								name="dataInicioAvaliacaoAgronomica"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
							{errors.dataInicioAvaliacaoAgronomica && <span className="error-field">{errors.dataInicioAvaliacaoAgronomica.message}</span>}
						</Form.Group>
					</Col>
					<Col md={3}>
						<Form.Group>
							<Form.Label>Data final</Form.Label>
							<Controller
								control={control}
								name="dataTerminoAvaliacaoAgronomica"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
							{errors.dataTerminoAvaliacaoAgronomica && <span className="error-field">{errors.dataTerminoAvaliacaoAgronomica.message}</span>}
						</Form.Group>
					</Col>
				</Row>

				<AssinaturaComNomesAdicionais
					label="assinatura"
					assinaturaUsuario={assinaturaUsuario}
					assinaturaBase64={assinaturaEnvolvidos}
					onAssinaturaBase64Change={setAssinaturaEnvolvidos}
					nomesAdicionais={nomesAdicionais}
					onNomesAdicionaisChange={setNomesAdicionais}
					mostrarAssinatura={mostrarAssinatura}
					onMostrarAssinaturaChange={setMostrarAssinatura}
					assinaturasDisponiveis={assinaturasDisponiveis}
				/>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Formulário de registro de avaliações e atividades agronômicas em contenção</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Tipo de material</Form.Label>
						<div className="d-flex flex-column">
							<Form.Check type="checkbox" ref={register} name="tipoMaterialAvaliadoGraos" label="Sementes / grãos" />
							<Form.Check type="checkbox" ref={register} name="tipoMaterialAvaliadoRaizes" label="Raiz" />
							<Form.Check type="checkbox" ref={register} name="tipoMaterialAvaliadoFolhas" label="Folha" />
							<Form.Check type="checkbox" ref={register} name="tipoMaterialAvaliadoVagem" label="Vagem" />
							<Form.Check type="checkbox" ref={register} name="tipoMaterialAvaliadoCapulho" label="Capulho e/ou caroço" />
							<Form.Check type="checkbox" ref={register} name="tipoMaterialAvaliadoOutros" label="Outros (especificar)" />
						</div>
						<Form.Control
							className="mt-2"
							ref={register}
							name="outrosTipoMaterialAvaliado"
							placeholder="Descreva o material quando a opção 'Outros' for selecionada"
							maxLength={200}
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Tipo de avaliação / atividade agronômica realizada</Form.Label>
						<div className="d-flex flex-column">
							<Form.Check type="checkbox" ref={register} name="tipoAvaliacaoPesagem" label="Pesagem" />
							<Form.Check type="checkbox" ref={register} name="tipoAvaliacaoDeterminacaoUmidade" label="Determinação umidade grão/semente" />
							<Form.Check type="checkbox" ref={register} name="tipoAvaliacaoContagem" label="Contagem sementes" />
							<Form.Check type="checkbox" ref={register} name="tipoAvaliacaoDescarocamento" label="Descaroçamento" />
							<Form.Check type="checkbox" ref={register} name="tipoAvaliacaoDeterminacaoPluma" label="Determinação % de pluma" />
							<Form.Check type="checkbox" ref={register} name="tipoAvaliacaoPreparoAmostras" label="Somente preparo de amostras armazenamento e/ou transporte para outra localidade" />
							<Form.Check type="checkbox" ref={register} name="tipoAvaliacaoTratamento" label="Tratamento de sementes" />
							<Form.Check type="checkbox" ref={register} name="tipoAvaliacaoMoagem" label="Moagem" />
							<Form.Check type="checkbox" ref={register} name="tipoAvaliacaoOutros" label="Outras (especificar)" />
						</div>
						<Form.Control
							className="mt-2"
							ref={register}
							name="outrosTipoAvaliacao"
							placeholder="Descreva a avaliação quando a opção 'Outras' for selecionada"
							maxLength={200}
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Formulário de limpeza - equipamentos utilizados nas avaliações / atividades agronômicas</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom font-weight-bold">
					<Col md={4}>Nome e código do equipamento utilizado</Col>
					<Col md={2}>Data de limpeza – Antes do uso</Col>
					<Col md={2}>Data de limpeza – Após o uso</Col>
					<Col md={2}>Método de limpeza</Col>
					<Col md={2}>Responsável</Col>
				</Row>

				{Array.from({ length: quantidadeLinhasEquipamentos }).map((_, index) => {
					const metodoSelecionado = watch(`equipamentoMetodo${index}` as any);
					return (
					<Row className="row-custom align-items-end" key={`equipamento-${index}`}>
						<Col md={4}>
							<Form.Control ref={register} name={`equipamentoNome${index}`} placeholder="Equipamento" maxLength={150} />
						</Col>
						<Col md={2}>
							<Controller
								control={control}
								name={`equipamentoDataAntes${index}` as any}
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Col>
						<Col md={2}>
							<Controller
								control={control}
								name={`equipamentoDataDepois${index}` as any}
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Col>
						<Col md={2}>
							<MetodoLimpezaSelect
								register={register}
								name={`equipamentoMetodo${index}`}
								nameOutros={`equipamentoMetodoOutros${index}`}
								metodoSelecionado={metodoSelecionado}
							/>
						</Col>
						<Col md={2}>
							<Form.Control ref={register} name={`equipamentoResponsavel${index}`} placeholder="Responsável" maxLength={150} />
						</Col>
						<Col md="auto" className="d-flex justify-content-end">
							{quantidadeLinhasEquipamentos > 1 && (
								<Button title="Remover" contained={false} type="button" onClick={() => removerLinhaEquipamento(index)} />
							)}
						</Col>
					</Row>
					);
				})}

				<div className="button-position" style={{ justifyContent: 'flex-start' }}>
					<Button title="Adicionar equipamento" contained={false} type="button" onClick={adicionarLinhaEquipamento} />
				</div>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Formulário de limpeza - instalações de contenção utilizadas nas avaliações / atividades agronômicas</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom font-weight-bold">
					<Col md={6}>Nome e código da sala / instalação</Col>
					<Col md={3}>Data de limpeza</Col>
					<Col md={3}>Responsável</Col>
				</Row>

				{Array.from({ length: quantidadeLinhasInstalacoes }).map((_, index) => (
					<Row className="row-custom align-items-end" key={`instalacao-${index}`}>
						<Col md={6}>
							<Form.Control ref={register} name={`instalacaoNome${index}`} placeholder="Sala / Instalação" maxLength={150} />
						</Col>
						<Col md={3}>
							<Controller
								control={control}
								name={`instalacaoData${index}` as any}
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Col>
						<Col md={3}>
							<Form.Control ref={register} name={`instalacaoResponsavel${index}`} placeholder="Responsável" maxLength={150} />
						</Col>
						<Col md="auto" className="d-flex justify-content-end">
							{quantidadeLinhasInstalacoes > 1 && (
								<Button title="Remover" contained={false} type="button" onClick={() => removerLinhaInstalacao(index)} />
							)}
						</Col>
					</Row>
				))}

				<div className="button-position mb-3" style={{ justifyContent: 'flex-start' }}>
					<Button title="Adicionar instalação" contained={false} type="button" onClick={adicionarLinhaInstalacao} />
				</div>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Observações</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							ref={register}
							name="observacoesAvaliacaoAgronomica"
							placeholder="Registre observações complementares sobre as atividades realizadas"
							maxLength={500}
						/>
						{errors.observacoesAvaliacaoAgronomica && <span className="error-field">{errors.observacoesAvaliacaoAgronomica.message}</span>}
					</Form.Group>
				</Form.Row>

				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title="Salvar" contained={true} type="submit" />
				</div>
			</Form>
		</Modal>
	);
}

